import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from '@heroicons/react/outline';
import Button from './Button';

// Cookie helper functions
const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "; expires=" + date.toUTCString();
  document.cookie = name + "=" + (value || "") + expires + "; path=/; SameSite=Lax";
};

const getCookie = (name: string) => {
  const nameEQ = name + "=";
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

const ConsultationPopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    company: '',
    interest: 'other',
    message: 'Requested a free system architecture consultation from the popup.',
  });

  useEffect(() => {
    // Check if the user has already seen or interacted with the popup using cookies
    const hasSeenPopup = getCookie('procreative_consultation_seen');
    
    if (!hasSeenPopup) {
      // Show the popup after 8 seconds
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 8000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  const closePopup = () => {
    setIsVisible(false);
    setCookie('procreative_consultation_seen', 'true', 365);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/contact/enquiry', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formState,
          phone: 'N/A' // Fallback for required fields
        }),
      });

      if (response.ok) {
        setIsSuccess(true);
        // Set cookie immediately upon submission success
        setCookie('procreative_consultation_seen', 'true', 365);
        setTimeout(() => {
          setIsVisible(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to submit consultation request:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.95 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 w-[calc(100%-2rem)] md:w-full md:max-w-sm"
        >
          <div className="relative bg-card dark:bg-dark-card border border-border/60 dark:border-dark-border/60 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
            {/* Abstract Background pattern */}
            <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)', backgroundSize: '16px 16px' }}></div>
            
            {/* Close button */}
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                closePopup();
              }}
              className="absolute top-4 right-4 text-foreground/50 hover:text-foreground dark:text-dark-foreground/50 dark:hover:text-dark-foreground transition-colors z-20 p-2 cursor-pointer hover:bg-secondary/40 dark:hover:bg-dark-secondary/30 rounded-full"
              aria-label="Close"
            >
              <XIcon className="w-5 h-5" />
            </button>
            
            <div className="p-6 relative z-10">
              {isSuccess ? (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4 border border-success/20">
                    <svg className="w-6 h-6 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-foreground dark:text-dark-foreground">Request Received</h3>
                  <p className="text-sm text-foreground/70 dark:text-dark-foreground/70 mt-2 font-light">
                    An architect will be in touch shortly.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-6">
                    <span className="text-[9px] uppercase tracking-widest font-semibold text-accent font-heading">Limited Offer</span>
                    <h3 className="text-xl font-bold text-foreground dark:text-dark-foreground mt-1">
                      Free Architecture <span className="font-serif italic text-accent font-light">Audit</span>
                    </h3>
                    <p className="text-xs text-foreground/70 dark:text-dark-foreground/70 mt-2 font-light leading-relaxed">
                      Discover how custom software can eliminate your operational bottlenecks. Enter your details for a complimentary systems consultation.
                    </p>
                  </div>
                  
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="Your Name"
                        required
                        value={formState.name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-input dark:bg-dark-input border border-border/50 dark:border-dark-border/50 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent dark:text-dark-foreground transition-shadow placeholder:text-foreground/40 dark:placeholder:text-dark-foreground/40"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="Work Email"
                        required
                        value={formState.email}
                        onChange={handleChange}
                        className="w-full px-3 py-2 bg-input dark:bg-dark-input border border-border/50 dark:border-dark-border/50 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-accent dark:text-dark-foreground transition-shadow placeholder:text-foreground/40 dark:placeholder:text-dark-foreground/40"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      variant="accent" 
                      className="w-full rounded-lg text-xs uppercase tracking-wide font-semibold"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Requesting...' : 'Claim Free Audit'}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConsultationPopup;
